// Records-page body for an Amazon Category scrape: shows the input config the
// scrape ran with + the discovered product list (category scrapes have no ASIN
// rows, so the normal ASIN table would just say "No ASINs"). Driven by the
// amazon/category/result/ payload (config, schedule_status, asins, ...).

import { useState } from "react";
import { McaPagination, PAGE_SIZE } from "../mca";

const CURRENCY = {
  "amazon.com.au": "AUD", "amazon.com": "USD", "amazon.co.uk": "GBP",
  "amazon.ca": "CAD", "amazon.de": "EUR", "amazon.fr": "EUR", "amazon.it": "EUR",
  "amazon.es": "EUR", "amazon.nl": "EUR", "amazon.com.mx": "MXN",
  "amazon.com.br": "BRL", "amazon.co.jp": "JPY", "amazon.in": "INR",
  "amazon.sg": "SGD", "amazon.ae": "AED", "amazon.sa": "SAR",
  "amazon.com.tr": "TRY", "amazon.se": "SEK", "amazon.pl": "PLN",
  "amazon.eg": "EGP", "amazon.co.za": "ZAR", "amazon.ie": "EUR",
};

const fmtPrice = (cents, domain) => {
  if (cents == null) return "—";
  const cur = CURRENCY[domain] || "USD";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
};

const Row = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
    <span className="cell-muted" style={{ fontSize: "12px" }}>{label}</span>
    <span className="cell-strong" style={{ wordBreak: "break-word" }}>{children}</span>
  </div>
);

export const CategoryRecords = ({ result, onRefresh }) => {
  const config = result?.config || {};
  const asins = Array.isArray(result?.asins) ? result.asins : [];
  const status = result?.schedule_status;
  const domain = config.domain || "";
  const isComplete =
    !!result?.is_download_generated || result?.download_links?.stage === "complete";

  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(asins.length / PAGE_SIZE));
  const curPage = Math.min(page, pageCount - 1);
  const pageRows = asins.slice(curPage * PAGE_SIZE, curPage * PAGE_SIZE + PAGE_SIZE);

  const priceMin =
    config.price_min_cents ? fmtPrice(config.price_min_cents, domain) : "No min";
  const priceMax =
    config.price_max_cents && config.price_max_cents < 10000000
      ? fmtPrice(config.price_max_cents, domain)
      : "No max";

  return (
    <>
      {/* Input config card */}
      <div className="mca-card mca-card--pad" style={{ marginBottom: "16px" }}>
        <div className="mca-card__title" style={{ marginBottom: "14px" }}>
          Category scrape input
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "16px",
          }}
        >
          <Row label="Amazon domain">{domain || "—"}</Row>
          <Row label="Category node">{config.amazon_node_id || "—"}</Row>
          <Row label="Postcode">{config.postcode || "—"}</Row>
          <Row label="Price range">{priceMin} – {priceMax}</Row>
          <Row label="Prime ships from AU">
            {config.filter_prime_ships_from_au ? "Yes" : "No"}
          </Row>
          <Row label="Delivery filter">{config.filter_delivery_day || "—"}</Row>
        </div>
        {config.category_url && (
          <div
            style={{
              marginTop: "14px",
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              minWidth: 0,
            }}
          >
            <span className="cell-muted" style={{ fontSize: "12px" }}>Category URL</span>
            <a
              href={config.category_url}
              target="_blank"
              rel="noopener noreferrer"
              className="cell-strong"
              title={config.category_url}
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
            >
              {config.category_url}
            </a>
          </div>
        )}
      </div>

      {/* Result body */}
      {status === "Failed" ? (
        <div className="mca-card mca-card--pad" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div className="cell-strong" style={{ color: "var(--mca-danger)", marginBottom: "6px" }}>
            This category scrape failed
          </div>
          <div className="cell-muted">{result?.failed_message || "No further detail available."}</div>
        </div>
      ) : !isComplete ? (
        <div className="mca-card mca-card--pad" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div className="cell-strong" style={{ marginBottom: "6px" }}>
            Scrape in progress…
          </div>
          <div className="cell-muted" style={{ marginBottom: "12px" }}>
            Runs on the category queue and may wait for an available worker; this
            page updates when it finishes.
          </div>
          <button className="mca-btn mca-btn--outline mca-btn--sm" type="button" onClick={onRefresh}>
            Refresh
          </button>
        </div>
      ) : asins.length === 0 ? (
        <div className="mca-card mca-card--pad" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div className="cell-strong">No products found for these filters.</div>
        </div>
      ) : (
        <>
        <div className="mca-table-wrap">
          <table className="mca-table">
            <thead>
              <tr>
                <th>ASIN</th>
                <th>Parent</th>
                <th>Price</th>
                <th className="hide-md">Prime</th>
                <th>In stock</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p, i) => (
                <tr key={`${p.asin}-${i}`}>
                  <td className="mca-cell-mono cell-strong">
                    {domain ? (
                      <a href={`https://${domain}/dp/${p.asin}`} target="_blank" rel="noopener noreferrer">
                        {p.asin}
                      </a>
                    ) : (p.asin || "—")}
                  </td>
                  <td className="mca-cell-mono cell-muted">
                    {p.parent_asin || "—"}
                    {p.is_variant && (
                      <span className="mca-badge mca-badge--soft-muted" style={{ marginLeft: "6px" }}>
                        variant
                      </span>
                    )}
                  </td>
                  <td>{fmtPrice(p.price_cents, domain)}</td>
                  <td className="hide-md">
                    {p.is_prime ? (
                      <span className="mca-badge mca-badge--soft-info">Prime</span>
                    ) : "—"}
                  </td>
                  <td>
                    <span className={`mca-badge ${p.in_stock ? "mca-badge--soft-success" : "mca-badge--soft-danger"}`}>
                      {p.in_stock ? "In stock" : "Out"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {asins.length > PAGE_SIZE && (
          <div className="table-foot">
            <span>
              Showing {curPage * PAGE_SIZE + 1}–
              {Math.min((curPage + 1) * PAGE_SIZE, asins.length)} of {asins.length} products
            </span>
            <McaPagination
              page={curPage + 1}
              totalPages={pageCount}
              onChange={(p) => setPage(p - 1)}
            />
          </div>
        )}
        </>
      )}
    </>
  );
};
